'use client';

import { lazy, Suspense, useState, type ComponentPropsWithRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { NotifyMeTrigger } from '@/features/notify-me';
import { QuickBuy } from '@/features/quick-buy';
import { useDeferredUnmount } from '@/hooks/use-deferred-unmount';
import { useProduct } from '@/providers/product-context';
import { ProductButton as ProductButtonView } from '@/shared-ui/organisms/product-button';
import { useProductAddToBag } from './hooks/use-product-add-to-bag';
import { resolveBuyInAppTarget } from './lib/buy-in-app-target';
import { getActionProductId, getBuyInAppData, resolveProductButton } from './lib/resolve-product-button';
import type { ProductButtonKind, ProductButtonState } from './lib/types';

type Product = ShopperProducts.schemas['Product'];
type Variant = ShopperProducts.schemas['Variant'];

const BuyInAppModal = lazy(() => import('@/features/buy-in-app').then((module) => ({ default: module.BuyInAppModal })));

// i18n key + English fallback per kind; the label never reaches `ProductButtonState` (analytics).
const LABELS: Record<ProductButtonKind | 'adding', { key: string; fallback: string }> = {
    'add-to-bag': { key: 'addToBag', fallback: 'Add to Bag' },
    adding: { key: 'addingToBag', fallback: 'Adding to Bag...' },
    'buy-in-app': { key: 'buyInApp', fallback: 'Buy in App' },
    'notify-me': { key: 'notifyMe', fallback: 'Notify Me' },
    'quick-buy': { key: 'quickBuy', fallback: 'Quick Buy' },
    'out-of-stock': { key: 'outOfStock', fallback: 'Out of Stock' },
};

export type ProductButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'onClick'> & {
    /** Defaults to the product from `ProductProvider`. */
    product?: Product;
    /** Forces a kind, bypassing product data. */
    kind?: ProductButtonKind;
    selectedVariant?: Variant | null;
    context?: 'pdp' | 'plp';
    layout?: 'full' | 'icon';
    size?: 'sm' | 'default' | 'lg';
    currency?: string;
    /** Fires on every click before the built-in behaviour, e.g. for analytics. */
    onAction?: (state: ProductButtonState, product: Product) => void;
    /** Replaces the built-in behaviour for the given kinds. */
    actionOverrides?: Partial<Record<ProductButtonKind, () => void>>;
};

/** Product CTA that picks its kind from product data and runs the matching action. */
export const ProductButton = ({ product: productProp, ...props }: ProductButtonProps) => {
    const contextProduct = useProduct();
    const product = productProp ?? contextProduct;
    if (!product) return null;
    return <ProductButtonAction product={product} {...props} />;
};
ProductButton.displayName = 'ProductButton';

const ProductButtonAction = ({
    product,
    kind,
    selectedVariant,
    context = 'plp',
    layout = 'full',
    currency,
    onAction,
    actionOverrides,
    ...buttonProps
}: ProductButtonProps & { product: Product }) => {
    const { t } = useTranslation('productButton');
    const buttonState = resolveProductButton(product, { kind, selectedVariant, context });
    const productId = getActionProductId(product, selectedVariant);
    const { addToBag, isAdding } = useProductAddToBag(productId);
    const [isBuyInAppOpen, setBuyInAppOpen] = useState(false);
    const isBuyInAppMounted = useDeferredUnmount(isBuyInAppOpen);
    const override = actionOverrides?.[buttonState.kind];
    const { optimoveUrl, qrCodeUrl } = getBuyInAppData(product);

    const label = LABELS[isAdding ? 'adding' : buttonState.kind];

    const handleBuyInApp = () => {
        const target = resolveBuyInAppTarget(navigator.userAgent, optimoveUrl);
        if (target.type === 'navigate') {
            window.location.assign(target.url);
            return;
        }
        setBuyInAppOpen(true);
    };

    const handleClick = () => {
        onAction?.(buttonState, product);
        if (override) {
            override();
        } else if (buttonState.kind === 'add-to-bag' && !buttonState.disabled) {
            void addToBag();
        } else if (buttonState.kind === 'buy-in-app') {
            handleBuyInApp();
        }
    };

    const button = (
        <ProductButtonView
            kind={buttonState.kind}
            label={t(label.key, { defaultValue: label.fallback })}
            context={context}
            layout={layout}
            isLoading={isAdding}
            onClick={handleClick}
            {...buttonProps}
            // After the spread: a caller may disable the CTA, never re-enable a restricted one.
            disabled={buttonState.disabled || Boolean(buttonProps.disabled)}
        />
    );

    if (!override && buttonState.kind === 'notify-me' && productId) {
        return <NotifyMeTrigger productId={productId}>{button}</NotifyMeTrigger>;
    }
    if (!override && buttonState.kind === 'quick-buy') {
        return (
            <QuickBuy productId={product.id} currency={currency}>
                {button}
            </QuickBuy>
        );
    }
    return (
        <>
            {button}
            {isBuyInAppMounted && (
                <Suspense fallback={null}>
                    <BuyInAppModal open={isBuyInAppOpen} onOpenChange={setBuyInAppOpen} qrCodeImageUrl={qrCodeUrl} />
                </Suspense>
            )}
        </>
    );
};
